import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from '../../api/client';
import type {
  Referral,
  ReferralJobTitle,
  ReferralSettings,
  ReferralWorker,
  SaveReferralRequest,
} from '../../api/types';
import { useHubContext } from '../../shell/HubContext';
import { MissedPayoutDialog } from './MissedPayoutDialog';
import { ReferralFormDialog } from './ReferralFormDialog';
import { ReferralTable } from './ReferralTable';
import { daysBetween, formatDate, formatMoney } from './referralDates';
import { compareBy, nextSort, type Sort, type SortKey } from './referralSort';

/** The one thing the chips narrow the table to. Mutually exclusive — each answers its own question. */
type Filter = 'all' | 'open' | 'action' | 'nextPayout' | 'notAnnounced';

/**
 * The chips, in the order the work runs in: decide, then watch the run it lands on approach.
 *
 * Built per render rather than held as a constant because the next run is a date, and a chip that says
 * which payroll run it means is worth more than one that says "next". The rest are fixed.
 */
function filtersFor(nextRun: string | null): { value: Filter; label: string; title: string }[] {
  return [
    { value: 'all', label: 'All', title: 'Every referral ever recorded' },
    { value: 'open', label: 'Open', title: 'Everything not yet finished' },
    {
      value: 'action',
      label: 'Needs a decision',
      title: 'Every referral with no eligibility decision on it yet',
    },
    {
      value: 'nextPayout',
      label: nextRun === null ? 'Next payout' : `Next payout · ${formatDate(nextRun)}`,
      title:
        nextRun === null
          ? 'Everything falling due on the next payroll run — the calendar has no run left, so this is empty'
          : `Everything falling due on the run of ${formatDate(nextRun)}, decided or not`,
    },
    {
      value: 'notAnnounced',
      label: 'Payroll not notified',
      title: 'Accepted and unpaid, and payroll has not been told about the run it falls on',
    },
  ];
}

/**
 * Whether one referral belongs under the chip that is pressed.
 *
 * Every question here is asked of the row as it stands rather than of the snapshot the order was
 * built from, because a filter that lags a click would hide the row somebody just acted on.
 *
 * No chip asks about the payout date having passed any more. Closing a referral out is a thing done
 * to a row rather than a list to work through, so it lives on the row — see the paid / not paid
 * buttons in `ReferralTable`, which appear wherever the row does.
 */
function matchesFilter(referral: Referral, filter: Filter, nextRun: string | null): boolean {
  if (filter === 'all') return true;
  if (filter === 'open') return !referral.completed;

  // Every row still waiting on the eligibility flag, whatever its dates say. Not only the ones whose
  // probation has passed: this is the list of decisions outstanding, and one that hid the referrals
  // coming up would be a list you could work to empty and still be behind.
  if (filter === 'action') return referral.eligibleForPayout === null && !referral.completed;

  // Everything landing on that run, not only what is already accepted. A chip that showed only the
  // settled rows would leave out the ones the run is actually waiting on — the undecided bonus due on
  // Friday is the whole reason to look at next Friday before it arrives.
  //
  // Finished rows are the one exclusion. A referral that was turned down still computes a payout date,
  // but it is not going on that run, and listing it under "next payout" would say it was.
  if (filter === 'nextPayout') {
    return nextRun !== null && referral.payoutDate === nextRun && !referral.completed;
  }

  return (
    referral.eligibleForPayout === true &&
    !referral.isPaid &&
    !referral.completed &&
    !referral.payoutAnnouncedToPayroll
  );
}

/**
 * How many days of warning HR gets, defaulting to none when the API does not say.
 *
 * An API older than this bundle does not send the field at all. Zero rather than whatever arithmetic
 * `undefined` produces: nothing is flagged, which is the honest answer when the number that decides
 * the flag is missing — and it keeps a version skew showing one fewer highlight rather than throwing
 * on a `NaN` date and taking the whole section down with it.
 */
function noticeLeadDays(settings: ReferralSettings): number {
  return Number.isFinite(settings.probationNoticeLeadDays) ? settings.probationNoticeLeadDays : 0;
}

/** What the dialog is doing: closed, adding, or editing the referral it carries. */
type Editing = { mode: 'closed' } | { mode: 'add' } | { mode: 'edit'; referral: Referral };

/**
 * The referral log — who referred whom, what it pays, and when it falls due. Replaces the shared
 * spreadsheet this section was specified from.
 */
export function EmployeeReferralSection() {
  const { api } = useHubContext();

  const [referrals, setReferrals] = useState<Referral[] | null>(null);

  /**
   * Display order, fixed when the list loads. Held apart from the rows themselves so that deciding on
   * a referral changes how it looks without moving it out from under the person who just clicked it.
   */
  const [order, setOrder] = useState<string[]>([]);
  const [jobTitles, setJobTitles] = useState<ReferralJobTitle[]>([]);

  /**
   * The payroll roster the referrer box suggests from. Empty is a working state, not a failure: the
   * API returns an empty list when the feed cannot be read, and the box is a text input either way.
   */
  const [workers, setWorkers] = useState<ReferralWorker[]>([]);
  const [settings, setSettings] = useState<ReferralSettings | null>(null);
  const [payoutDates, setPayoutDates] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing>({ mode: 'closed' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const [deciding, setDeciding] = useState<ReadonlySet<string>>(new Set());

  /** The referral whose missed payout is being asked about, if any. */
  const [missedFor, setMissedFor] = useState<Referral | null>(null);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>('all');

  /** Null is the urgency order the list loads in — see `visible`. */
  const [sort, setSort] = useState<Sort>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      // The job titles and the schedule are what the dialog needs to fill a payout in and to preview
      // the derived dates, so all three load together rather than the dialog fetching on open.
      const [referralList, jobTitleList, loadedSettings, payoutDateList, workerList] = await Promise.all([
        api.listReferrals(signal),
        api.listReferralJobTitles(signal),
        api.getReferralSettings(signal),
        api.listReferralPayoutDates(signal),
        api.listReferralWorkers(signal),
      ]);

      // The display order is decided here, once, and held until the next load — see `visible`.
      //
      // Missed runs first, then rows waiting on a decision, then ones coming due, then everything
      // else. Inside each of the three marked bands the 90-day date sorts ascending, so the row with
      // the least time left is the one at the top of it — the API's own order is newest hire first,
      // which inside a band puts the least urgent row above the most urgent one. Unmarked rows keep
      // that API order, because there the log's own newest-first reading is the point.
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

      // Mirrors the three marks ReferralTable draws, so the rows carrying one sort above the rows
      // that do not. Kept in step with that file by hand — they are the same three questions.
      const leadDays = noticeLeadDays(loadedSettings);

      const urgency = (r: Referral) => {
        const undecided = r.eligibleForPayout === null && !r.completed;
        const waiting = undecided && r.probationClearedOn <= day;

        if (waiting && r.payoutDate !== null && r.payoutDate < day) return 3;
        if (waiting) return 2;

        // Coming up: HR is about to be mailed about it, but there is nothing to decide yet — so it
        // sits under the rows that can be acted on rather than among them.
        return undecided && daysBetween(day, r.probationClearedOn) <= leadDays ? 1 : 0;
      };

      setReferrals(referralList.referrals);
      setOrder(
        [...referralList.referrals]
          .sort((a, b) => {
            const byUrgency = urgency(b) - urgency(a);
            if (byUrgency !== 0) return byUrgency;

            // Zero is returned rather than a comparison so the sort stays stable and the unmarked
            // rows come back in exactly the order the API sent them.
            if (urgency(a) === 0) return 0;

            return a.probationClearedOn.localeCompare(b.probationClearedOn);
          })
          .map((r) => r.id),
      );
      setJobTitles(jobTitleList.jobTitles);
      setSettings(loadedSettings);
      setPayoutDates(payoutDateList.payoutDates.map((d) => d.payoutOn));
      setWorkers(workerList.workers);
    },
    [api],
  );

  useEffect(() => {
    const controller = new AbortController();

    load(controller.signal).catch((error: unknown) => {
      if (controller.signal.aborted) return;

      setLoadError(error instanceof Error ? error.message : 'The referrals could not be loaded.');
    });

    return () => controller.abort();
  }, [load]);

  // Local calendar day as `yyyy-mm-dd`. Built from the local parts rather than `toISOString()`,
  // which converts to UTC first and so names yesterday for most of the evening in the Americas.
  const today = useMemo(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");

    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }, []);

  /**
   * The first payroll run on or after today — what the "next payout" chip lists, and what a missed
   * payout gets moved to.
   *
   * Declared above `visible` because the chip filters on it. Order matters in a component body in a
   * way it does not in a class: a `const` read before its own line is a temporal-dead-zone throw, not
   * an undefined.
   */
  const nextRun = useMemo(
    () =>
      payoutDates
        .filter((d) => d >= today)
        .reduce<string | null>((first, d) => (first === null || d < first ? d : first), null),
    [payoutDates, today],
  );

  const visible = useMemo(() => {
    if (referrals === null) return [];

    const term = search.trim().toLowerCase();

    const matching = referrals.filter((referral) => {
      if (!matchesFilter(referral, filter, nextRun)) return false;
      if (term === '') return true;

      return [referral.referringEmployeeName, referral.newHireName, referral.jobTitle]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });

    // A column was chosen, so that wins. Ties fall back to the new hire's name rather than being left
    // to the filter's accidental order — on a column like Paid, which has two values, almost every
    // comparison is a tie.
    if (sort !== null) {
      return [...matching].sort((a, b) => {
        const result = compareBy(a, b, sort.key);

        return (sort.descending ? -result : result) || a.newHireName.localeCompare(b.newHireName);
      });
    }

    // Otherwise the snapshot taken when the list loaded, not each row's current state.
    //
    // Sorting live would be worse than it sounds: deciding on a row changes its urgency, so the row
    // just clicked leaves the top of the list and lands somewhere far down it — which reads as the
    // record having been deleted. Rows settle into their new order on the next load, when nobody is
    // mid-click. Anything the snapshot has not seen (a referral just added) goes to the front.
    return [...matching].sort((a, b) => {
      const ia = order.indexOf(a.id);
      const ib = order.indexOf(b.id);

      return (ia === -1 ? -1 : ia) - (ib === -1 ? -1 : ib);
    });
  }, [referrals, search, filter, sort, order, nextRun]);

  // What is still owed, which is the question the spreadsheet was kept to answer. Only counts rows
  // HR has confirmed, and only those nobody has paid yet.
  const outstanding = useMemo(
    () =>
      (referrals ?? [])
        .filter((r) => !r.completed && r.eligibleForPayout === true)
        .reduce((total, r) => total + r.payoutAmount, 0),
    [referrals],
  );

  // Probation is up and nobody has confirmed the bonus — the rows waiting on HR.
  const awaitingConfirmation = useMemo(
    () =>
      (referrals ?? []).filter(
        (r) => r.eligibleForPayout === null && !r.completed && r.probationClearedOn <= today,
      ).length,
    [referrals, today],
  );


  /**
   * One row, one call, the answer patched back in. The shape every single-referral action takes —
   * held once so a new one cannot forget to clear the busy flag on the way out of a failure.
   *
   * Patched rather than reloaded on purpose: these all change exactly one row, and a full reload
   * would scroll a long table back to the top under somebody working through it.
   */
  async function act(
    referral: Referral,
    call: (id: string) => Promise<Referral>,
    whenItFails: string,
  ) {
    setDeciding((current) => new Set(current).add(referral.id));
    setSaveError(null);

    try {
      const updated = await call(referral.id);

      setReferrals((current) =>
        current === null ? current : current.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : whenItFails);
    } finally {
      setDeciding((current) => {
        const next = new Set(current);
        next.delete(referral.id);
        return next;
      });
    }
  }

  /**
   * HR's accept/turn-down, straight from the table. The row updates from the response rather than
   * reloading the list — the decision changes one row, and a full reload would scroll a long table
   * back to the top under someone working through it.
   */
  async function decide(referral: Referral, eligible: boolean | null) {
    await act(
      referral,
      (id) => api.setReferralEligibility(id, eligible),
      'The decision could not be saved.',
    );
  }

  /** Closes one referral out — paid or not paid, finished either way. The Closure list's whole job. */
  async function close(referral: Referral, paid: boolean) {
    await act(referral, (id) => api.closeReferral(id, paid), 'It could not be closed.');
  }

  /** Unlocks a finished referral so its decision and statuses can be changed again. */
  async function reopen(referral: Referral) {
    await act(referral, (id) => api.reopenReferral(id), 'It could not be reopened.');
  }

  /** The answer to "was this missed payout actually paid?" — either way the referral is closed out. */
  async function resolveMissed(paid: boolean) {
    if (missedFor === null) return;

    setDeciding((current) => new Set(current).add(missedFor.id));
    setSaveError(null);

    try {
      const updated = await api.resolveMissedPayout(missedFor.id, paid);

      setReferrals((current) =>
        current === null ? current : current.map((r) => (r.id === updated.id ? updated : r)),
      );
      setMissedFor(null);
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : 'It could not be saved.');
    } finally {
      setDeciding((current) => {
        const next = new Set(current);
        next.delete(missedFor.id);
        return next;
      });
    }
  }

  async function save(request: SaveReferralRequest) {
    setSaving(true);
    setSaveError(null);
    setFieldErrors({});

    try {
      if (editing.mode === 'edit') {
        await api.updateReferral(editing.referral.id, request);
      } else {
        await api.createReferral(request);
      }

      // Reloaded rather than merged into the list from the response: the derived dates come from the
      // API, and a list that is partly server-computed and partly stitched together here is how the
      // two drift apart.
      await load();
      setEditing({ mode: 'closed' });
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors);
        setSaveError(Object.keys(error.fieldErrors).length > 0 ? null : error.message);
      } else {
        setSaveError(error instanceof Error ? error.message : 'It could not be saved.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loadError !== null) {
    return (
      <div className="page-message page-message--error">
        <h2>The referrals could not be loaded</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  if (referrals === null || settings === null) {
    return <div className="page-message">Loading referrals…</div>;
  }

  return (
    <div className="section-page">
      <header className="section-page__header">
        <div>
          <h1>Employee Referral</h1>
          <p className="section-page__lead">
            {referrals.length} referral{referrals.length === 1 ? '' : 's'} ·{' '}
            {formatMoney(outstanding)} confirmed and still to pay
            {awaitingConfirmation > 0 && (
              <>
                {' · '}
                <strong>
                  {awaitingConfirmation} past {settings.probationDays} days awaiting confirmation
                </strong>
              </>
            )}
          </p>
        </div>

        <button
          type="button"
          className="button button--primary"
          onClick={() => {
            setSaveError(null);
            setFieldErrors({});
            setEditing({ mode: 'add' });
          }}
        >
          Add referral
        </button>
      </header>

      <div className="section-page__toolbar">
        <input
          className="control section-page__search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by team member, new hire or job title"
          aria-label="Search referrals"
        />

        <div className="section-page__chips" role="group" aria-label="Filter the referrals">
          {filtersFor(nextRun).map((option) => (
            <button
              key={option.value}
              type="button"
              className={
                filter === option.value
                  ? 'button button--quiet button--small is-selected'
                  : 'button button--quiet button--small'
              }
              title={option.title}
              aria-pressed={filter === option.value}
              onClick={() => setFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {sort !== null && (
          <span className="section-page__count">
            Sorted by column —{' '}
            <button type="button" className="link-button" onClick={() => setSort(null)}>
              back to urgency order
            </button>
          </span>
        )}
      </div>

      {/* A failed decision from the table has nowhere else to show — the dialog is not open for it. */}
      {editing.mode === 'closed' && saveError !== null && <p className="dialog__error">{saveError}</p>}

      {visible.length === 0 ? (
        <p className="section-page__empty">
          {referrals.length === 0
            ? 'No referrals recorded yet. Add the first one.'
            : 'No referrals match that.'}
        </p>
      ) : (
        <ReferralTable
          referrals={visible}
          today={today}
          noticeLeadDays={noticeLeadDays(settings)}
          sort={sort}
          onSort={(key: SortKey) => setSort((current) => nextSort(current, key))}
          deciding={deciding}
          onDecide={decide}
          onClose={close}
          onReopen={reopen}
          onResolveMissed={(referral) => {
            setSaveError(null);
            setMissedFor(referral);
          }}
          onEdit={(referral) => {
            setSaveError(null);
            setFieldErrors({});
            setEditing({ mode: 'edit', referral });
          }}
        />
      )}

      {missedFor !== null && (
        <MissedPayoutDialog
          key={missedFor.id}
          referral={missedFor}
          nextRun={nextRun}
          saving={deciding.has(missedFor.id)}
          error={saveError}
          onAnswer={resolveMissed}
          onClose={() => setMissedFor(null)}
        />
      )}

      {editing.mode !== 'closed' && (
        <ReferralFormDialog
          // Remounts between rows so the dialog's own state starts from the referral it was opened
          // on rather than from whichever one was open before it.
          key={editing.mode === 'edit' ? editing.referral.id : 'add'}
          referral={editing.mode === 'edit' ? editing.referral : null}
          jobTitles={jobTitles}
          workers={workers}
          settings={settings}
          payoutDates={payoutDates}
          today={today}
          saving={saving}
          fieldErrors={fieldErrors}
          error={saveError}
          onSave={save}
          onClose={() => setEditing({ mode: 'closed' })}
        />
      )}

    </div>
  );
}
